<script lang="ts">
  import Screen from '../lib/design/Screen.svelte';
  import { link, href } from '../lib/router.svelte';
  import { session } from '../lib/session.svelte';
  import { nest } from '../lib/nest.svelte';
  import { storageExportUrl } from '../lib/storage';
  import { humanize } from '../shared/slug.js';
  import { TYPE_DIRS, type PageType } from '../shared/coop-types.js';

  const TYPES = Object.keys(TYPE_DIRS) as PageType[];
  const PLURAL: Record<PageType, string> = {
    entity: 'entities',
    concept: 'concepts',
    source: 'sources',
  };

  type ReadTab = PageType | 'exports';

  let filter = $state<ReadTab>('concept');
  let q = $state('');

  const searching = $derived(Boolean(q.trim()));
  const results = $derived(searching ? nest.search(q) : filter === 'exports' ? [] : nest.byType(filter));
  const sortedExports = $derived(
    nest.exports.slice().sort((a, b) => b.modified.localeCompare(a.modified)),
  );

  function fmtSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function ago(ts: number | null): string {
    if (!ts) return 'never';
    const s = Math.round((Date.now() - ts) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86_400)}d ago`;
  }

  // --- pull to refresh -------------------------------------------------
  let pull = $state(0);
  let startY = 0;
  let tracking = false;
  const THRESHOLD = 70;

  function onTouchStart(e: TouchEvent) {
    if (window.scrollY > 0 || nest.status === 'syncing') return;
    startY = e.touches[0]?.clientY ?? 0;
    tracking = true;
  }
  function onTouchMove(e: TouchEvent) {
    if (!tracking) return;
    const dy = (e.touches[0]?.clientY ?? 0) - startY;
    pull = dy > 0 ? Math.min(dy * 0.5, 90) : 0;
  }
  function onTouchEnd() {
    if (tracking && pull >= THRESHOLD) void nest.pull();
    tracking = false;
    pull = 0;
  }
</script>

<svelte:window ontouchstart={onTouchStart} ontouchmove={onTouchMove} ontouchend={onTouchEnd} />

<Screen title="Read" sync={nest.status}>
  {#if pull > 0}
    <div class="ptr" style="height:{pull}px">
      {pull >= THRESHOLD ? 'Release to sync' : 'Pull to sync'}
    </div>
  {/if}

  {#if !session.state.dropboxConnected}
    <p class="hint">
      Connect Dropbox in <a use:link href={href('/settings')}>Settings</a> to load your nest.
    </p>
  {:else if nest.needsCoopPick}
    <p class="hint">Which folder is your coop?</p>
    <div class="coops">
      {#each nest.coops as c (c)}
        <button class="coop" onclick={() => nest.pickCoop(c)}>{c}</button>
      {/each}
    </div>
  {:else}
    <label class="search">
      <input
        type="search"
        bind:value={q}
        placeholder="Search the nest…"
        autocapitalize="none"
        spellcheck="false"
      />
    </label>

    {#if !searching}
      <div class="seg">
        {#each TYPES as t (t)}
          <button class:on={filter === t} onclick={() => (filter = t)}>{PLURAL[t]}</button>
        {/each}
        <button class:on={filter === 'exports'} onclick={() => (filter = 'exports')}>exports</button>
      </div>
    {/if}

    {#if !searching && filter === 'exports'}
      {#if sortedExports.length}
        <ul class="pages">
          {#each sortedExports as x (x.path)}
            <li>
              <a href={storageExportUrl(x.path)} download>
                <span class="title">{x.name}</span>
                <span class="badge">
                  {fmtSize(x.size)}{#if x.modified} · {ago(new Date(x.modified).getTime())}{/if}
                </span>
              </a>
            </li>
          {/each}
        </ul>
      {:else}
        <p class="hint">No exports yet. Docs, decks and charts Kip generates land here.</p>
      {/if}
    {:else if results.length}
      <ul class="pages">
        {#each results as p (p.slug)}
          <li>
            <a use:link href={href(`/page/${p.slug}`)}>
              <span class="title">{humanize(p.slug)}</span>
              {#if searching}<span class="badge">{PLURAL[p.type]}</span>{/if}
              {#if p.summary}<span class="summary">{p.summary}</span>{/if}
            </a>
          </li>
        {/each}
      </ul>
    {:else if nest.status === 'syncing' && nest.count === 0}
      <p class="hint">Pulling your nest…</p>
    {:else if nest.status === 'error' && nest.count === 0}
      <p class="hint err">{nest.error}</p>
      <button class="retry" onclick={() => nest.pull()}>Retry</button>
    {:else if searching}
      <p class="hint">No pages match “{q}”.</p>
    {:else if nest.count === 0}
      <p class="hint">Nothing in the nest yet.</p>
    {:else}
      <p class="hint">No {PLURAL[filter as PageType]}.</p>
    {/if}

    <p class="foot">
      {nest.count} page{nest.count === 1 ? '' : 's'} · synced {ago(nest.lastPulledAt)} ·
      <button class="linkish" onclick={() => nest.pull()} disabled={nest.status === 'syncing'}>
        {nest.status === 'syncing' ? 'syncing…' : 'sync now'}
      </button>
    </p>
  {/if}
</Screen>

<style>
  .ptr {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ink-faint);
    overflow: hidden;
    transition: height 0.12s;
  }
  .search input {
    width: 100%;
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 10px 14px;
    background: var(--cream-2);
    font-size: 16px;
    margin-bottom: 16px;
  }
  .seg {
    display: flex;
    border: 1px solid var(--line);
    border-radius: 999px;
    overflow: hidden;
    margin-bottom: 18px;
  }
  .seg button {
    flex: 1;
    padding: 8px 0;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .seg button.on {
    background: var(--ink);
    color: var(--cream);
  }
  .pages {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .pages a {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 12px 10px;
    border-radius: 10px;
  }
  .pages a:active {
    background: var(--cream-2);
  }
  .title {
    font-family: var(--serif);
    font-size: 17px;
  }
  .badge {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .summary {
    font-size: 13px;
    color: var(--ink-soft);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hint {
    color: var(--ink-soft);
    font-size: 14px;
    padding: 8px 0;
  }
  .hint.err {
    color: var(--rooster-deep);
  }
  .hint a {
    color: var(--rooster);
    text-decoration: underline;
  }
  .coops {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .coop {
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 12px 14px;
    background: var(--cream-2);
    font-family: var(--mono);
    font-size: 13px;
    text-align: left;
  }
  .retry {
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 8px 16px;
    font-family: var(--mono);
    font-size: 12px;
  }
  .foot {
    margin-top: 22px;
    font-size: 12px;
    color: var(--ink-faint);
  }
  .linkish {
    color: var(--rooster);
    font: inherit;
  }
  .linkish:disabled {
    color: var(--ink-faint);
  }
</style>
